import { formatDate } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { Maybe } from 'src/app/core/nullable-utils';
import { RoleAssignmentDto, RoleName } from 'src/app/core/services/user-api.service';
import { capitalize } from 'src/app/shared/utils/function';

@Pipe({ name: 'userSetupFilter' })
export class UserSetupFilterPipe implements PipeTransform {
  readonly #searchColumns = ['name', 'username', 'roleAssignments'] as const;

  transform<T extends {}>(items: readonly T[], searchTexts: readonly string[]): T[] {
    if (!items?.length) {
      return [];
    }

    if (!searchTexts?.length || !searchTexts[0]) {
      return [...items];
    }

    return items.filter(item => {
      return this.#numberOfMatches(searchTexts, item) === searchTexts.length;
    });
  }

  #numberOfMatches(searchTexts: readonly string[], item: Record<string, unknown>): number {
    return searchTexts.filter(searchText => this.#searchTextInRoleColumn(item, searchText)).length;
  }

  #searchTextInRoleColumn(item: Record<string, unknown>, searchText: string): boolean {
    const lowerSearchText = searchText.toLowerCase();

    return this.#searchColumns.some(column => {
      const value = item[column];

      if (column === 'roleAssignments') {
        return this.#roleContainsSearchText(lowerSearchText, value as RoleAssignmentDto[]);
      }

      if (typeof value === 'string') {
        return value.toLowerCase().includes(lowerSearchText);
      }

      return false;
    });
  }

  #roleContainsSearchText(searchText: string, roles?: RoleAssignmentDto[]): boolean {
    if (!roles?.length) {
      return false;
    }

    return roles.some((role: RoleAssignmentDto) => {
      return (
        formatRoleName(role.roleName)?.toLowerCase().includes(searchText) ||
        role.project?.name?.toLowerCase()?.includes(searchText) ||
        role.trimPackage?.code?.toLowerCase()?.includes(searchText) ||
        role.status?.toLowerCase()?.includes(searchText) ||
        (role.validFrom && formatDate(role.validFrom, 'dd.MM.yy HH:mm', 'en')?.includes(searchText)) ||
        (role.validTo && formatDate(role.validTo, 'dd.MM.yy HH:mm', 'en')?.includes(searchText))
      );
    });
  }
}

@Pipe({ name: 'rolePrettyPrint' })
export class RolePrettyPrintPipe implements PipeTransform {
  transform(role: Maybe<RoleName>): string {
    return role ? formatRoleName(role) : '';
  }
}

function formatRoleName(roleName: string): string {
  return roleName
    .replace(/^ROLE_/, '') // Remove 'ROLE_' prefix
    .toLowerCase()
    .split('_')
    .map(capitalize)
    .join(' ');
}
